import { useState } from "react";
import { Plus, Truck, Save, Trash2, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { fmtDate, round2, uuid, vals } from "@/lib/format";
import { BigButton, Chip, Empty, Field, PageTitle, inputCls } from "@/components/ui-kit";
import SupplierDetail from "@/components/purchases/SupplierDetail";
import useScannerInput from "@/lib/useScannerInput";
import { AttachmentField, openFile } from "@/components/files/FileFields";
import DataGrid from "@/components/DataGrid";
import useIsDesktop from "@/lib/useIsDesktop";
import { Paperclip } from "lucide-react";

function SupplierDialog({ open, onOpenChange }) {
  const { t, dispatch } = useApp();
  const [f, setF] = useState({ name: "", phone: "" });
  const save = () => {
    if (!f.name.trim()) return;
    if (dispatch("supplier.upsert", { id: uuid(), name: f.name.trim(), phone: f.phone.trim(), notes: "" })) {
      toast.success(t("saved"));
      setF({ name: "", phone: "" });
      onOpenChange(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="supplier-dialog">
        <DialogTitle className="font-heading text-2xl">{t("add_supplier")}</DialogTitle>
        <Field label={t("name")}><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="supplier-name-input" /></Field>
        <Field label={t("phone")}><input dir="ltr" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="supplier-phone-input" /></Field>
        <BigButton onClick={save} className="w-full" data-testid="supplier-save-btn"><Save className="w-5 h-5" />{t("save")}</BigButton>
      </DialogContent>
    </Dialog>
  );
}

function NewPurchase({ suppliers, products }) {
  const { t, dispatch, money } = useApp();
  const [supplierId, setSupplierId] = useState(null);
  const [lines, setLines] = useState([]);
  const [paid, setPaid] = useState("");
  const total = round2(lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.cost || 0), 0));
  const addLine = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (p && !lines.some((l) => l.product_id === pid)) setLines([...lines, { product_id: p.id, name: p.name, qty: "1", cost: String(p.cost || "") }]);
  };
  const upd = (pid, k, v) => setLines(lines.map((l) => (l.product_id === pid ? { ...l, [k]: v } : l)));
  useScannerInput((code) => {
    const p = products.find((x) => x.barcode === code);
    if (p) { addLine(p.id); toast.success(p.name); } else toast.error(`${t("not_found_barcode")}: ${code}`);
  });
  const save = () => {
    const sup = suppliers.find((s) => s.id === supplierId);
    const items = lines.filter((l) => Number(l.qty) > 0).map((l) => ({ ...l, qty: Number(l.qty), cost: Number(l.cost || 0) }));
    const data = { id: uuid(), supplier_id: sup.id, supplier_name: sup.name, items, total, paid: paid === "" ? total : Number(paid), attachment_id: att };
    if (dispatch("purchase.create", data)) { toast.success(t("purchase_saved")); setLines([]); setPaid(""); setAtt(""); }
  };
  const [att, setAtt] = useState("");

  return (
    <div className="space-y-4" data-testid="new-purchase-panel">
      <Field label={t("supplier")}>
        <div className="flex gap-2 flex-wrap">
          {suppliers.map((s) => <Chip key={s.id} active={supplierId === s.id} onClick={() => setSupplierId(s.id)} data-testid={`purchase-supplier-${s.id}`}><Truck className="w-4 h-4" />{s.name}</Chip>)}
          {!suppliers.length && <span className="text-muted-foreground">{t("add_supplier_first")}</span>}
        </div>
      </Field>
      <Select value="" onValueChange={addLine}>
        <SelectTrigger className="h-12 rounded-xl bg-white font-bold" data-testid="purchase-add-product-select"><PackagePlus className="w-5 h-5" /><SelectValue placeholder={t("add_product_line")} /></SelectTrigger>
        <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id} className="py-3">{p.name}</SelectItem>)}</SelectContent>
      </Select>
      {lines.map((l) => (
        <div key={l.product_id} className="rounded-xl bg-white border border-border p-3 grid grid-cols-[1fr_80px_100px_44px] gap-2 items-center" data-testid={`purchase-line-${l.product_id}`}>
          <span className="font-bold truncate">{l.name}</span>
          <input dir="ltr" inputMode="decimal" aria-label={t("qty")} value={l.qty} onChange={(e) => upd(l.product_id, "qty", e.target.value)} className={`${inputCls} text-center px-1`} data-testid="purchase-line-qty" />
          <input dir="ltr" inputMode="decimal" aria-label={t("cost")} value={l.cost} onChange={(e) => upd(l.product_id, "cost", e.target.value)} className={`${inputCls} text-center px-1`} data-testid="purchase-line-cost" />
          <button type="button" onClick={() => setLines(lines.filter((x) => x !== l))} className="h-11 rounded-lg bg-secondary grid place-items-center" data-testid="purchase-line-remove"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {lines.length > 0 && <div className="text-xs text-muted-foreground">{t("qty")} · {t("cost")}</div>}
      <div className="flex justify-between items-end rounded-xl bg-secondary p-3"><span className="font-bold">{t("total")}</span><span className="text-2xl font-extrabold" data-testid="purchase-total">{money(total)}</span></div>
      <Field label={t("paid_supplier")}><input dir="ltr" inputMode="decimal" placeholder={String(total)} value={paid} onChange={(e) => setPaid(e.target.value)} className={inputCls} data-testid="purchase-paid-input" /></Field>
      <AttachmentField value={att} onChange={setAtt} testid="purchase-attachment" />
      <BigButton disabled={!supplierId || !lines.length} onClick={save} className="w-full" data-testid="purchase-save-btn"><Save className="w-5 h-5" />{t("save_purchase")}</BigButton>
    </div>
  );
}

export default function Purchases() {
  const { view, t, money, lang } = useApp();
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState(null);
  const suppliers = vals(view.suppliers).sort((a, b) => a.name.localeCompare(b.name));
  const products = vals(view.products).filter((p) => !p.deleted).sort((a, b) => a.name.localeCompare(b.name));
  const history = vals(view.purchases).sort((a, b) => b.created_at.localeCompare(a.created_at));
  const isDesktop = useIsDesktop();

  return (
    <div>
      <PageTitle testid="purchases-title" action={<BigButton variant="soft" onClick={() => setAdding(true)} data-testid="supplier-add-btn"><Plus className="w-5 h-5" />{t("add_supplier")}</BigButton>}>{t("purchases")}</PageTitle>
      <Tabs defaultValue="new">
        <TabsList className="w-full h-12 mb-4">
          <TabsTrigger value="new" className="flex-1 h-10 font-bold" data-testid="purchases-tab-new">{t("new_purchase")}</TabsTrigger>
          <TabsTrigger value="history" className="flex-1 h-10 font-bold" data-testid="purchases-tab-history">{t("history")}</TabsTrigger>
          <TabsTrigger value="suppliers" className="flex-1 h-10 font-bold" data-testid="purchases-tab-suppliers">{t("suppliers")}</TabsTrigger>
        </TabsList>
        <TabsContent value="new"><NewPurchase suppliers={suppliers} products={products} /></TabsContent>
        <TabsContent value="history" className="space-y-2">
          {history.length === 0 && !isDesktop && <Empty icon={Truck} text={t("no_history")} />}
          {isDesktop && (
            <DataGrid testid="purchases-grid" name="purchases" rows={history} dateField="created_at"
              columns={[
                { key: "created_at", label: t("date"), get: (p) => fmtDate(p.created_at, lang) }, { key: "supplier_name", label: t("supplier") },
                { key: "items", label: t("items"), get: (p) => p.items.map((i) => `${i.name} ×${i.qty}`).join(", ") },
                { key: "staff_name", label: t("staff") }, { key: "paid", label: t("paid"), align: "end", sum: true }, { key: "total", label: t("total"), align: "end", sum: true },
              ]}
              actions={(p) => p.attachment_id && <button type="button" onClick={() => openFile(p.attachment_id)} className="w-7 h-7 rounded hover:bg-[#DCE6EE] grid place-items-center" aria-label={t("view")} data-testid="purchase-attachment-open"><Paperclip className="w-4 h-4" /></button>} />
          )}
          {!isDesktop && history.map((p) => (
            <div key={p.id} className="rounded-xl bg-white border border-border p-3" data-testid="purchase-history-row">
              <div className="flex justify-between font-bold"><span>{p.supplier_name}</span><span>{money(p.total)}</span></div>
              <div className="text-sm text-muted-foreground">{fmtDate(p.created_at, lang)} · {p.items.map((i) => `${i.name} ×${i.qty}`).join("، ")}</div>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="suppliers" className="space-y-2">
          {suppliers.length === 0 && <Empty icon={Truck} text={t("no_suppliers")} />}
          {suppliers.map((s) => (
            <button type="button" key={s.id} onClick={() => setOpenId(s.id)} className="w-full min-h-[64px] rounded-xl bg-white border border-border p-3 flex justify-between items-center text-start hover:border-primary transition-colors" data-testid="supplier-row" data-supplier-id={s.id}>
              <div><div className="font-bold">{s.name}</div><div className="text-sm text-muted-foreground" dir="ltr">{s.phone}</div></div>
              <div className="text-end"><div className="text-xs text-muted-foreground">{t("we_owe")}</div><div className={`font-extrabold ${s.balance > 0 ? "text-destructive" : "text-[#1F9D55]"}`} data-testid="supplier-row-balance">{money(s.balance || 0)}</div></div>
            </button>
          ))}
        </TabsContent>
      </Tabs>
      <SupplierDialog open={adding} onOpenChange={setAdding} />
      <SupplierDetail supplierId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
